'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { MapPin, MessageCircle, Navigation, Check, Loader2, ChevronRight } from 'lucide-react';
import dynamic from 'next/dynamic';
import type { EclipseExperience } from '@/components/map/EclipseMap';

const EclipseMap = dynamic(() => import('@/components/map/EclipseMap'), {
  ssr: false,
  loading: () => (
    <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--pub-surface-2)' }}>
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: '2px solid var(--pub-border)',
          borderTopColor: 'var(--pub-green)',
          animation: 'pub-spin 1s linear infinite',
        }}
      />
    </div>
  ),
});

interface ExperienceCardProps {
  scanId: string;
  code: string;
}

type CardPhase =
  | 'loading'           // initial — polling for background geolocation
  | 'estimated'         // IP location resolved — show approximate pin
  | 'no_location'       // background geolocation failed/timed out
  | 'sharing'           // user tapped "Share Location" — requesting browser geolocation
  | 'picking_location'  // user denied geolocation — picking on fullscreen map
  | 'precise'           // user shared precise location — show pin
  | 'adding_message'    // user is writing a message
  | 'complete'          // message sent

interface PrimaryLocation {
  latitude: number;
  longitude: number;
  location: string | null;
  message: string | null;
  scanId: string;
}

interface ScanLocation {
  latitude: number | null;
  longitude: number | null;
  location: string | null;
  hasLocation: boolean;
  message: string | null;
  primaryLocation?: PrimaryLocation | null;
}

const POLL_INTERVAL = 3000;
const POLL_TIMEOUT = 15000;

export function ExperienceCard({ scanId, code }: ExperienceCardProps) {
  const [phase, setPhase] = useState<CardPhase>('loading');
  const [scanData, setScanData] = useState<ScanLocation | null>(null);
  const [preciseLocation, setPreciseLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mapPickedLocation, setMapPickedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [mapExperiences, setMapExperiences] = useState<EclipseExperience[]>([]);
  const [mapImageUrl, setMapImageUrl] = useState<string | null>(null);
  const [mapViewOpen, setMapViewOpen] = useState(false);
  const [primaryLocation, setPrimaryLocation] = useState<PrimaryLocation | null>(null);
  const pollStartRef = useRef(Date.now());

  // ── The "current" coordinates (precise > primary > estimated) ───────
  const currentCoords = preciseLocation
    ? { lat: preciseLocation.lat, lng: preciseLocation.lng }
    : primaryLocation
      ? { lat: primaryLocation.latitude, lng: primaryLocation.longitude }
      : scanData?.latitude && scanData?.longitude
        ? { lat: scanData.latitude, lng: scanData.longitude }
        : null;

  // ── Fetch static map thumbnail when we have coordinates ─────────────
  useEffect(() => {
    if (!currentCoords) return;

    let cancelled = false;

    fetch('/api/map/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lat: currentCoords.lat,
        lng: currentCoords.lng,
        zoom: 11,
        width: 400,
        height: 180,
        pinColor: 'f59e0b',
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        setMapImageUrl(URL.createObjectURL(blob));
      })
      .catch(() => {});

    return () => { cancelled = true; };
    // Only refetch when coords actually change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCoords?.lat, currentCoords?.lng]);

  // ── Poll scan record for background geolocation ────────────────────────
  const pollScan = useCallback(async () => {
    try {
      const res = await fetch(`/api/scans/${scanId}`);
      if (!res.ok) return null;
      return (await res.json()) as ScanLocation;
    } catch {
      return null;
    }
  }, [scanId]);

  useEffect(() => {
    if (phase !== 'loading') return;

    let cancelled = false;
    const poll = async () => {
      const data = await pollScan();
      if (cancelled) return;

      if (data) {
        setScanData(data);

        // If the code already has a shared location from a previous scan,
        // use it as the primary and skip straight to showing it
        if (data.primaryLocation) {
          setPrimaryLocation(data.primaryLocation);
          setPhase('precise');
          return;
        }

        if (data.hasLocation) {
          setPhase('estimated');
          return;
        }
        if (data.message) {
          setPhase('complete');
          return;
        }
      }

      if (Date.now() - pollStartRef.current > POLL_TIMEOUT) {
        setPhase('no_location');
        return;
      }

      setTimeout(poll, POLL_INTERVAL);
    };

    poll();
    return () => { cancelled = true; };
  }, [phase, pollScan]);

  // ── Fetch map experiences when map picker or map view opens ──────────
  useEffect(() => {
    if (phase !== 'picking_location' && !mapViewOpen) return;

    fetch('/api/eclipse/map?limit=1000')
      .then((r) => r.json())
      .then((data) => setMapExperiences(data.experiences || []))
      .catch(() => {});
  }, [phase, mapViewOpen]);

  // ── Share precise location ─────────────────────────────────────────────
  const shareLocation = useCallback(async () => {
    setPhase('sharing');
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setPhase(scanData?.hasLocation ? 'estimated' : 'no_location');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setPreciseLocation({ lat: latitude, lng: longitude });

        try {
          setSubmitting(true);
          const res = await fetch('/api/eclipse/share', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ scanId, latitude, longitude }),
          });

          if (!res.ok) throw new Error('Failed to share location');
          setPhase('precise');
        } catch {
          setError('Failed to save location. Please try again.');
          setPhase(scanData?.hasLocation ? 'estimated' : 'no_location');
        } finally {
          setSubmitting(false);
        }
      },
      (err) => {
        switch (err.code) {
          case err.PERMISSION_DENIED:
          case err.POSITION_UNAVAILABLE:
          case err.TIMEOUT:
            setError(null);
            setPhase('picking_location');
            return;
        }
        setPhase(scanData?.hasLocation ? 'estimated' : 'no_location');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }, [scanId, scanData]);

  // ── Submit message ─────────────────────────────────────────────────────
  const submitMessage = useCallback(async () => {
    if (!message.trim()) return;

    try {
      setSubmitting(true);
      setError(null);

      const res = await fetch('/api/eclipse/share', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scanId, message: message.trim() }),
      });

      if (!res.ok) throw new Error('Failed to save message');
      setPhase('complete');
    } catch {
      setError('Failed to save message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [scanId, message]);

  // ── Confirm map-picked location ────────────────────────────────────────
  const confirmMapLocation = useCallback(async () => {
    if (!mapPickedLocation) return;

    try {
      setSubmitting(true);
      setError(null);

      const res = await fetch('/api/eclipse/share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scanId,
          latitude: mapPickedLocation.lat,
          longitude: mapPickedLocation.lng,
        }),
      });

      if (!res.ok) throw new Error('Failed to share location');

      setPreciseLocation(mapPickedLocation);
      setPhase('precise');
    } catch {
      setError('Failed to save location. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [scanId, mapPickedLocation]);

  // ── Computed ───────────────────────────────────────────────────────────
  const locationDisplay = preciseLocation
    ? `${preciseLocation.lat.toFixed(2)}, ${preciseLocation.lng.toFixed(2)}`
    : primaryLocation?.location || (primaryLocation ? `${primaryLocation.latitude.toFixed(2)}, ${primaryLocation.longitude.toFixed(2)}` : null)
      || scanData?.location || (scanData?.latitude ? `${scanData.latitude.toFixed(2)}, ${scanData.longitude?.toFixed(2)}` : null);

  const hasCoords = !!currentCoords;

  const mapPickerExperiences: EclipseExperience[] = [
    ...mapExperiences,
    ...(mapPickedLocation ? [{
      id: 'temp-location',
      codeId: '',
      latitude: mapPickedLocation.lat,
      longitude: mapPickedLocation.lng,
      scannedAt: new Date().toISOString(),
      isPublic: false,
    }] : []),
  ];

  return (
    <>
      <div
        className="pub-card pub-reveal pub-reveal-3"
        style={{
          padding: '18px 16px',
          background: 'linear-gradient(155deg, rgba(245,158,11,0.06) 0%, transparent 55%)',
        }}
      >
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div
            className="flex items-center justify-center rounded-full shrink-0"
            style={{
              width: 36,
              height: 36,
              background: 'rgba(245,158,11,0.12)',
            }}
          >
            <MapPin size={18} className="text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">
              Share your eclipse viewing spot
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Pin your location on the community map
            </p>
          </div>
        </div>

        {/* ── Static map thumbnail — shown whenever we have coords ────── */}
        {hasCoords && mapImageUrl && (
          <button
            onClick={() => setMapViewOpen(true)}
            className="w-full mb-3 relative overflow-hidden block"
            style={{
              borderRadius: 14,
              border: '1px solid var(--pub-border)',
              height: 120,
              cursor: 'pointer',
            }}
          >
            <img
              src={mapImageUrl}
              alt="Your location"
              className="w-full h-full object-cover"
            />
            {/* Location overlay pill */}
            <div
              className="absolute bottom-2 left-2 flex items-center gap-1.5"
              style={{
                background: 'rgba(0,0,0,0.65)',
                backdropFilter: 'blur(8px)',
                borderRadius: 100,
                padding: '4px 10px',
                fontSize: 10,
                color: '#fff',
                fontFamily: 'var(--font-pub-mono)',
                letterSpacing: '0.04em',
              }}
            >
              <MapPin size={10} className="text-amber-400" />
              {scanData?.location || locationDisplay}
            </div>
            {/* "View on map" hint */}
            <div
              className="absolute top-2 right-2 flex items-center gap-1"
              style={{
                background: 'rgba(0,0,0,0.55)',
                backdropFilter: 'blur(8px)',
                borderRadius: 100,
                padding: '3px 8px',
                fontSize: 9,
                color: 'rgba(255,255,255,0.8)',
                fontFamily: 'var(--font-pub-mono)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              View on map
              <ChevronRight size={10} />
            </div>
          </button>
        )}

        {/* Geolocating indicator */}
        {phase === 'loading' && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3 pl-1">
            <Loader2 size={12} className="animate-spin text-amber-500" />
            <span>Estimating your location...</span>
          </div>
        )}

        {/* Estimated location badge (no thumbnail yet) */}
        {phase === 'estimated' && !mapImageUrl && locationDisplay && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3 pl-1">
            <Navigation size={12} className="text-amber-500" />
            <span>Estimated: {locationDisplay}</span>
            <span className="text-[10px] opacity-60">(approximate)</span>
          </div>
        )}

        {/* Precise location badge */}
        {phase === 'precise' && locationDisplay && (
          <div className="flex items-center gap-2 text-xs mb-3 pl-1">
            <Check size={12} className="text-green-500" />
            <span className="text-green-600 dark:text-green-400">Location shared{!mapImageUrl ? `: ${locationDisplay}` : ''}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <p className="text-xs text-red-500 mb-2 pl-1">{error}</p>
        )}

        {/* Actions */}
        {phase !== 'complete' && phase !== 'adding_message' && phase !== 'picking_location' && (
          <div className="flex flex-col gap-2">
            {/* Primary CTA — Share Location */}
            {phase !== 'precise' && phase !== 'sharing' && (
              <button
                onClick={shareLocation}
                disabled={submitting}
                className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl text-sm font-medium transition-colors"
                style={{
                  background: 'rgba(245,158,11,0.12)',
                  color: 'rgb(245,158,11)',
                }}
              >
                <MapPin size={16} />
                Share your precise location
                <ChevronRight size={14} className="ml-auto opacity-50" />
              </button>
            )}

            {/* Sharing state */}
            {phase === 'sharing' && (
              <div className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl text-sm text-muted-foreground">
                <Loader2 size={16} className="animate-spin" />
                Requesting location...
              </div>
            )}

            {/* Add message button */}
            {(phase === 'precise' || phase === 'estimated' || phase === 'no_location') && (
              <button
                onClick={() => setPhase('adding_message')}
                className="flex items-center justify-center gap-2 w-full py-2 px-4 rounded-xl text-xs text-muted-foreground transition-colors hover:bg-muted/50"
              >
                <MessageCircle size={14} />
                Add a message to your pin
              </button>
            )}
          </div>
        )}

        {/* Message input */}
        {phase === 'adding_message' && (
          <div className="flex flex-col gap-2">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="How was your eclipse experience?"
              maxLength={280}
              rows={2}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-amber-500/50"
            />
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">{message.length}/280</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPhase(preciseLocation ? 'precise' : scanData?.hasLocation ? 'estimated' : 'no_location')}
                  className="px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:bg-muted/50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitMessage}
                  disabled={!message.trim() || submitting}
                  className="px-4 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-40"
                  style={{
                    background: 'rgba(245,158,11,0.15)',
                    color: 'rgb(245,158,11)',
                  }}
                >
                  {submitting ? <Loader2 size={12} className="animate-spin" /> : 'Share'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Complete state */}
        {phase === 'complete' && (
          <div className="flex items-center gap-2 text-xs pl-1">
            <Check size={14} className="text-green-500" />
            <span className="text-green-600 dark:text-green-400">
              Your experience has been shared on the map!
            </span>
          </div>
        )}
      </div>

      {/* ── Fullscreen map picker (location denied fallback) ──────────── */}
      {phase === 'picking_location' && (
        <div
          className="fixed inset-0 z-[200] flex flex-col"
          style={{ background: 'var(--pub-surface)' }}
        >
          <div
            className="flex justify-between items-center flex-shrink-0"
            style={{ padding: '52px 20px 12px' }}
          >
            <div>
              <h2
                style={{
                  fontFamily: 'var(--font-pub-display)',
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: 'var(--pub-text-primary)',
                }}
              >
                Pick your location
              </h2>
              <p
                style={{
                  fontFamily: 'var(--font-pub-mono)',
                  fontSize: 10,
                  color: 'var(--pub-text-muted)',
                  marginTop: 3,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                Tap the map to place your pin
              </p>
            </div>
            <button
              onClick={() => {
                setMapPickedLocation(null);
                setPhase(scanData?.hasLocation ? 'estimated' : 'no_location');
              }}
              className="flex items-center justify-center cursor-pointer transition-colors duration-200"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                border: '1px solid var(--pub-border)',
                background: 'var(--pub-surface-2)',
                color: 'var(--pub-text-secondary)',
              }}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div
            className="flex-1 relative overflow-hidden"
            style={{
              margin: '0 16px',
              borderRadius: 20,
              border: '1px solid var(--pub-border)',
            }}
          >
            <EclipseMap
              experiences={mapPickerExperiences}
              isEditMode
              onMapClick={(lat, lng) => setMapPickedLocation({ lat, lng })}
              initialCenter={
                scanData?.latitude && scanData?.longitude
                  ? [scanData.latitude, scanData.longitude]
                  : undefined
              }
              className="w-full h-full"
            />

            {mapPickedLocation && (
              <div
                className="absolute top-3 left-3 z-10 flex items-center gap-2"
                style={{
                  background: 'var(--pub-surface)',
                  border: '1px solid var(--pub-border)',
                  borderRadius: 100,
                  padding: '6px 14px',
                  fontFamily: 'var(--font-pub-mono)',
                  fontSize: 10,
                  color: 'var(--pub-text-secondary)',
                  letterSpacing: '0.04em',
                }}
              >
                <MapPin size={12} className="text-amber-500" />
                {mapPickedLocation.lat.toFixed(4)}, {mapPickedLocation.lng.toFixed(4)}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 flex-shrink-0" style={{ padding: '14px 16px 32px' }}>
            <button
              onClick={confirmMapLocation}
              disabled={!mapPickedLocation || submitting}
              className="pub-btn pub-btn-primary flex items-center justify-center gap-2 disabled:opacity-40"
            >
              {submitting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <>
                  <MapPin size={16} />
                  {mapPickedLocation ? 'Confirm this location' : 'Tap the map to place a pin'}
                </>
              )}
            </button>
            <button
              onClick={() => {
                setMapPickedLocation(null);
                setPhase(scanData?.hasLocation ? 'estimated' : 'no_location');
              }}
              className="pub-btn pub-btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Fullscreen map view (tap thumbnail to view pin) ───────────── */}
      {mapViewOpen && currentCoords && (
        <div
          className="fixed inset-0 z-[200] flex flex-col"
          style={{ background: 'var(--pub-surface)' }}
        >
          <div
            className="flex justify-between items-center flex-shrink-0"
            style={{ padding: '52px 20px 12px' }}
          >
            <div>
              <h2
                style={{
                  fontFamily: 'var(--font-pub-display)',
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: 'var(--pub-text-primary)',
                }}
              >
                Your viewing spot
              </h2>
              <p
                style={{
                  fontFamily: 'var(--font-pub-mono)',
                  fontSize: 10,
                  color: 'var(--pub-text-muted)',
                  marginTop: 3,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {scanData?.location || locationDisplay}
              </p>
            </div>
            <button
              onClick={() => setMapViewOpen(false)}
              className="flex items-center justify-center cursor-pointer transition-colors duration-200"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                border: '1px solid var(--pub-border)',
                background: 'var(--pub-surface-2)',
                color: 'var(--pub-text-secondary)',
              }}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div
            className="flex-1 relative overflow-hidden"
            style={{
              margin: '0 16px',
              borderRadius: 20,
              border: '1px solid var(--pub-border)',
            }}
          >
            <EclipseMap
              experiences={mapExperiences}
              highlightedId={code}
              initialCenter={[currentCoords.lat, currentCoords.lng]}
              className="w-full h-full"
            />
          </div>

          <div className="flex flex-col gap-2 flex-shrink-0" style={{ padding: '14px 16px 32px' }}>
            <button
              onClick={() => setMapViewOpen(false)}
              className="pub-btn pub-btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
